import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { APTITUDE_RANKS, getAptitudeImage, getAptitudeBadgeStyle } from '../../../../utils/aptitudeRanks';

/* ── Rank selector dropdown ─────────────────────────────────────────────── */
const AptitudeDropdown = ({ rank, onSelect }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.88, y: -8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.88, y: -5 }}
        transition={{ type: 'spring', stiffness: 450, damping: 28 }}
        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 p-3 w-44
            bg-white dark:bg-gray-900
            border border-gray-200 dark:border-gray-700
            rounded-2xl shadow-xl z-[100] backdrop-blur-2xl overflow-hidden"
    >
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#48E5B9]/30 to-transparent" />
        <div className="grid grid-cols-4 gap-1.5">
            {APTITUDE_RANKS.map(r => (
                <button key={r} onClick={e => { e.stopPropagation(); onSelect(r); }}
                    className={`
                        flex items-center justify-center w-9 h-9 rounded-xl p-1.5
                        transition-all duration-150
                        ${rank === r ? 'ring-2 ring-[#48E5B9] ring-offset-1 ring-offset-white dark:ring-offset-gray-900 scale-110' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}
                        ${getAptitudeBadgeStyle(r)}
                    `}
                >
                    <img src={getAptitudeImage(r)} alt={r} className="w-full h-full object-contain" />
                </button>
            ))}
        </div>
    </motion.div>
);

/* ── Main component ─────────────────────────────────────────────────────── */
const RacePlannerAptitudes = ({ aptitudes, onAptitudeChange }) => {
    const { t } = useTranslation();
    const [openKey, setOpenKey] = useState(null);

    const categories = [
        { id: 'track',    label: t('AptitudePanel.category_track'),    keys: ['turf','dirt'],                                      color: '#FAB0C0' },
        { id: 'distance', label: t('AptitudePanel.category_distance'), keys: ['short_distance','mile','medium_distance','long_distance'], color: '#48E5B9' },
        { id: 'style',    label: t('AptitudePanel.category_style'),    keys: ['runner','leader','betweener','chaser'],              color: '#9ca3af' },
    ];

    useEffect(() => {
        const close = e => { if (!e.target.closest('.apt-item')) setOpenKey(null); };
        window.addEventListener('click', close);
        return () => window.removeEventListener('click', close);
    }, []);

    const AptitudeItem = ({ aptKey, label }) => {
        const rank    = aptitudes[aptKey] || '-';
        const img     = getAptitudeImage(rank);
        const isOpen  = openKey === aptKey;

        return (
            <div className={`relative apt-item ${isOpen ? 'z-[110]' : ''}`}>
                <button
                    onClick={e => { e.stopPropagation(); setOpenKey(isOpen ? null : aptKey); }}
                    className={`
                        group relative flex flex-col items-center justify-center rounded-2xl p-2.5
                        w-[72px] h-[72px] border transition-all duration-200 overflow-hidden
                        ${isOpen
                            ? 'bg-gray-50 dark:bg-gray-800 border-[#48E5B9]/60 shadow-[0_0_18px_rgba(72,229,185,0.15)]'
                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }
                    `}
                >
                    {isOpen && <div className="absolute inset-0 bg-gradient-to-b from-[#48E5B9]/[0.06] to-transparent pointer-events-none" />}
                    <span className="text-[7px] uppercase tracking-wide font-black mb-1.5 w-full px-0.5 text-center leading-tight line-clamp-2
                        text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors">
                        {label}
                    </span>
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center p-1.5 transition-transform duration-200
                        ${isOpen ? 'scale-110' : 'group-hover:scale-105'}
                        ${getAptitudeBadgeStyle(rank)}`}>
                        {img
                            ? <img src={img} alt={rank} className="w-full h-full object-contain" />
                            : <span className="text-gray-300 dark:text-gray-600 text-base font-black">—</span>
                        }
                    </div>
                </button>

                <AnimatePresence>
                    {isOpen && (
                        <AptitudeDropdown
                            rank={rank}
                            onSelect={r => { onAptitudeChange(aptKey, r); setOpenKey(null); }}
                        />
                    )}
                </AnimatePresence>
            </div>
        );
    };

    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {categories.map((cat, i) => (
                <motion.div key={cat.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                    className="space-y-3">
                    <h3 className="flex items-center gap-2 text-[8px] font-black uppercase tracking-[0.35em] text-gray-400 dark:text-gray-500">
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                        {cat.label}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {cat.keys.map(k => (
                            <AptitudeItem key={k} aptKey={k} label={t(`AptitudeValues.${k}`)} />
                        ))}
                    </div>
                </motion.div>
            ))}
        </div>
    );
};

export default RacePlannerAptitudes;
